import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Mail, ShieldCheck, MapPin, ImagePlus, Loader2, Send, CheckCircle2, Star, Flag, MessageSquarePlus } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import WriteReviewSheet from "../components/WriteReviewSheet";

export default function ServiceProfessional() {
    const { userId } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewMeta, setReviewMeta] = useState({ rating: 0, count: 0 });
    const [reviewOpen, setReviewOpen] = useState(false);

    const loadReviews = () => api.get("/reviews", { params: { target_id: userId, limit: 50 } })
        .then((r) => {
            const list = r.data || [];
            setReviews(list);
            const count = list.length;
            const avg = count ? list.reduce((s, x) => s + (x.rating || 0), 0) / count : 0;
            setReviewMeta({ rating: Math.round(avg * 10) / 10, count });
        })
        .catch(() => setReviews([]));

    useEffect(() => {
        api.get(`/services/professionals/${userId}`).then((r) => {
            setData(r.data);
            // Prefer server-computed rating/reviews_count (authoritative aggregate).
            if (typeof r.data?.reviews_count === "number" && r.data.reviews_count > 0) {
                setReviewMeta({ rating: Math.round((r.data.rating || 0) * 10) / 10, count: r.data.reviews_count });
            }
        }).catch(() => setData({ notFound: true }));
        loadReviews();
    }, [userId]);

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
    }, [user]);

    const submitEnquiry = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
        setSending(true);
        try {
            await api.post(`/services/professionals/${userId}/enquire`, form);
            setSent(true);
            toast.success("Enquiry sent — the professional will contact you soon.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to send enquiry");
        } finally { setSending(false); }
    };

    if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-navy-500/50"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    if (data.notFound) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
                <div>
                    <div className="text-navy-500/50 mb-3">Service professional not found</div>
                    <Link to="/" className="text-gold-700 underline">Back to Home</Link>
                </div>
            </div>
        );
    }

    const callHref = data.phone ? `tel:${data.phone.replace(/\s+/g, "")}` : null;
    const whatsAppHref = data.phone
        ? `https://wa.me/${data.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${data.name}, I found you on Global Property Mart and would like to enquire about your services.`)}`
        : null;
    const mailHref = data.email ? `mailto:${data.email}?subject=${encodeURIComponent("Enquiry from Global Property Mart")}` : null;

    return (
        <div className="min-h-[70vh] bg-cream-50" data-testid="service-professional-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-4 pb-6">
                <div className="flex items-center gap-3 mb-4">
                    <Link to="/services" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0" aria-label="Back">
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-gold-400 font-bold">{data.category_label}</div>
                        <h1 className="font-heading font-bold text-xl truncate">{data.name}</h1>
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap text-[13px] text-white/80">
                    {data.city && (
                        <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-gold-400" /> {data.city}
                        </span>
                    )}
                    {data.verified && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-widest">
                            <ShieldCheck className="w-3 h-3" /> Verified
                        </span>
                    )}
                    {reviewMeta.count > 0 && (
                        <span
                            data-testid="sp-rating-summary"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold-500/15 border border-gold-500/40 text-gold-300 text-[11px] font-extrabold"
                        >
                            <Star className="w-3 h-3 fill-gold-400 text-gold-400" /> {reviewMeta.rating.toFixed(1)}
                            <span className="text-gold-200/80 font-semibold">· {reviewMeta.count} Review{reviewMeta.count !== 1 ? "s" : ""}</span>
                        </span>
                    )}
                </div>
            </div>

            <div className="px-3 sm:px-6 max-w-4xl mx-auto -mt-4 pb-8 space-y-4">
                {/* Contact card */}
                <div className="bg-white rounded-2xl shadow-sm border border-navy-500/8 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-navy-500/50 font-bold mb-2">Get in touch</div>
                    <div className="grid grid-cols-3 gap-2">
                        <a href={callHref || "#"} onClick={(e) => { if (!callHref) { e.preventDefault(); toast.info("Phone not shared by this professional"); } }} data-testid="sp-call-btn" className="flex flex-col items-center gap-1 py-3 rounded-xl bg-emerald-500 text-white hover:bg-emerald-400">
                            <Phone className="w-4 h-4" />
                            <span className="text-[11px] font-extrabold uppercase tracking-widest">Call</span>
                        </a>
                        <a href={whatsAppHref || "#"} target="_blank" rel="noreferrer" onClick={(e) => { if (!whatsAppHref) { e.preventDefault(); toast.info("WhatsApp not shared"); } }} data-testid="sp-whatsapp-btn" className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#25D366] text-white hover:opacity-90">
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-[11px] font-extrabold uppercase tracking-widest">WhatsApp</span>
                        </a>
                        <a href={mailHref || "#"} onClick={(e) => { if (!mailHref) { e.preventDefault(); toast.info("Email not shared"); } }} data-testid="sp-email-btn" className="flex flex-col items-center gap-1 py-3 rounded-xl bg-gold-500 text-navy-500 hover:bg-gold-400">
                            <Mail className="w-4 h-4" />
                            <span className="text-[11px] font-extrabold uppercase tracking-widest">Email</span>
                        </a>
                    </div>
                </div>

                {/* About */}
                {data.bio && (
                    <div className="bg-white rounded-2xl shadow-sm border border-navy-500/8 p-4">
                        <div className="text-[10px] uppercase tracking-widest text-navy-500/50 font-bold mb-2">About</div>
                        <p className="text-sm text-navy-500/80 leading-relaxed whitespace-pre-line">{data.bio}</p>
                    </div>
                )}

                {/* Get Quote / Send Enquiry inline form */}
                <div className="bg-white rounded-2xl shadow-sm border-2 border-gold-500/40 p-4" data-testid="sp-enquiry-form">
                    <div className="text-[10px] uppercase tracking-widest text-gold-700 font-extrabold mb-2">Get Quote / Send Enquiry</div>
                    {sent ? (
                        <div className="text-center py-4">
                            <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-2">
                                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                            </div>
                            <div className="font-heading font-bold text-navy-500 text-base">Enquiry sent!</div>
                            <div className="text-sm text-navy-500/60 mt-1">{data.name} will get in touch with you shortly.</div>
                        </div>
                    ) : (
                        <form onSubmit={submitEnquiry} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Your name *" data-testid="sp-enq-name" className="rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60" required />
                                <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Phone *" data-testid="sp-enq-phone" className="rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60" required inputMode="tel" />
                            </div>
                            <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email (optional)" data-testid="sp-enq-email" className="w-full rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60" />
                            <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Describe your requirement (e.g. 3BHK interior, title verification of plot in Wardha Road…)" rows={3} data-testid="sp-enq-message" className="w-full rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60 resize-none" />
                            <button type="submit" disabled={sending} data-testid="sp-enq-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gold-500 text-navy-500 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-60">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {sending ? "Sending…" : "Send Enquiry"}
                            </button>
                        </form>
                    )}
                </div>

                {/* Reviews */}
                <ReviewsSection
                    reviews={reviews}
                    meta={reviewMeta}
                    loggedIn={Boolean(user)}
                    canReview={user?.id !== data.id}
                    onWriteReview={() => setReviewOpen(true)}
                />

                {/* Portfolio */}
                <div className="bg-white rounded-2xl shadow-sm border border-navy-500/8 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] uppercase tracking-widest text-navy-500/50 font-bold">Portfolio · {data.portfolio.length} photo{data.portfolio.length !== 1 ? "s" : ""}</div>
                    </div>
                    {data.portfolio.length === 0 ? (
                        <div className="py-8 text-center">
                            <ImagePlus className="w-10 h-10 text-navy-500/25 mx-auto mb-2" />
                            <div className="text-sm text-navy-500/50">No portfolio photos uploaded yet.</div>
                        </div>
                    ) : (
                        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sp-portfolio-grid">
                            {data.portfolio.map((p) => (
                                <li key={p.id} className="rounded-lg overflow-hidden bg-navy-500/5 cursor-pointer group" onClick={() => setLightbox(p)}>
                                    <div className="aspect-square relative">
                                        <img src={fileUrl(p.image_url)} alt={p.title || "Portfolio"} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                                    </div>
                                    {p.title && (
                                        <div className="p-2">
                                            <div className="text-xs font-bold text-navy-500 truncate">{p.title}</div>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
                    <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
                        <img src={fileUrl(lightbox.image_url)} alt={lightbox.title || ""} className="w-full max-h-[80vh] object-contain rounded-lg" />
                        {(lightbox.title || lightbox.description) && (
                            <div className="mt-3 bg-white rounded-lg p-3">
                                {lightbox.title && <div className="font-heading font-bold text-navy-500">{lightbox.title}</div>}
                                {lightbox.description && <div className="text-sm text-navy-500/70 mt-1">{lightbox.description}</div>}
                            </div>
                        )}
                    </div>
                    <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center hover:bg-white" aria-label="Close">
                        ×
                    </button>
                </div>
            )}

            {/* Write a Review sheet */}
            <WriteReviewSheet
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                targetId={data.id}
                targetType={data.role}
                targetName={data.name}
                onSubmitted={() => loadReviews()}
            />
        </div>
    );
}


function ReviewsSection({ reviews, meta, loggedIn, canReview, onWriteReview }) {
    const [reported, setReported] = useState({}); // review_id -> true after report

    const onReport = async (id) => {
        if (!loggedIn) { toast.info("Please log in to report a review"); return; }
        const reason = window.prompt("Why is this review inappropriate?", "");
        if (reason === null) return; // cancelled
        try {
            await api.post(`/reviews/${id}/report`, { reason: (reason || "").trim() || undefined });
            setReported((r) => ({ ...r, [id]: true }));
            toast.success("Thanks — an admin will review this shortly.");
        } catch {
            toast.error("Could not submit report");
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-navy-500/8 p-4" data-testid="sp-reviews-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="text-[10px] uppercase tracking-widest text-navy-500/50 font-bold">Customer reviews</div>
                    {meta.count > 0 && (
                        <div className="inline-flex items-center gap-1 text-navy-500 text-sm font-extrabold">
                            <Star className="w-4 h-4 fill-gold-500 text-gold-500" /> {meta.rating.toFixed(1)}
                            <span className="text-navy-500/60 font-semibold text-xs">| {meta.count} Review{meta.count !== 1 ? "s" : ""}</span>
                        </div>
                    )}
                </div>
                {canReview && (
                    <button
                        type="button"
                        onClick={onWriteReview}
                        data-testid="sp-write-review-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold shadow"
                        style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                    >
                        <MessageSquarePlus className="w-3.5 h-3.5" /> Write a Review
                    </button>
                )}
            </div>

            {reviews.length === 0 ? (
                <div className="py-6 text-center">
                    <Star className="w-8 h-8 text-navy-500/25 mx-auto mb-1.5" />
                    <div className="text-sm text-navy-500/55">No reviews yet — be the first to work with them and share your experience.</div>
                    {canReview && (
                        <button
                            type="button"
                            onClick={onWriteReview}
                            data-testid="sp-write-first-review-btn"
                            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-extrabold shadow"
                            style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                        >
                            <MessageSquarePlus className="w-3.5 h-3.5" /> Write the First Review
                        </button>
                    )}
                </div>
            ) : (
                <ul className="space-y-3" data-testid="sp-reviews-list">
                    {reviews.map((r) => (
                        <li key={r.id} className="rounded-lg border border-navy-500/10 p-3 bg-cream-50/60" data-testid={`sp-review-${r.id}`}>
                            <div className="flex items-start gap-2 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-heading font-bold text-navy-500 text-sm">{r.user_name || "Anonymous"}</div>
                                        {r.verified && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-[9px] font-extrabold uppercase tracking-widest" data-testid={`sp-review-verified-${r.id}`}>
                                                <ShieldCheck className="w-2.5 h-2.5" /> Verified Review
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 mt-1">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                            <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? "fill-gold-500 text-gold-500" : "text-navy-500/20"}`} />
                                        ))}
                                        <span className="text-[10px] text-navy-500/50 ml-1">{new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
                                    </div>
                                    {r.comment && <p className="text-[13px] text-navy-500/75 leading-relaxed mt-2">{r.comment}</p>}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onReport(r.id)}
                                    disabled={reported[r.id]}
                                    data-testid={`sp-review-report-${r.id}`}
                                    className="text-navy-500/40 hover:text-red-500 p-1 rounded-md disabled:opacity-40"
                                    title="Report this review"
                                >
                                    <Flag className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
